import styled from '@emotion/styled'

import { Flex, PaddingInset, spacing, Text } from '@cash-design-system/react'
import {
  FormBlockerElementTimelineElement,
  FormBlockerElementTimelineElementEvent,
  FormBlockerElementTimelineElementEventIcon,
  FormBlockerElementTimelineElementEventInlineTextFormat,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'

import { BORDER_WIDTH, CIRCLE_SIZE, TimelineEventIcon } from './TimelineEventIcon'

const TimelineEventWrapper = styled.div`
  display: grid;
  grid-template-columns: 2rem 1fr;
  position: relative;
`

const Line = styled.div<{ lineColor: 'primary' | 'inputOutline' }>`
  position: absolute;
  background: ${({ theme, lineColor }) => theme.colors[lineColor]};
  width: ${BORDER_WIDTH};
  height: calc(100% + ${BORDER_WIDTH});
  top: calc(${CIRCLE_SIZE} - (${BORDER_WIDTH} / 2));
  left: calc((${CIRCLE_SIZE} / 2) - (${BORDER_WIDTH} / 2));
`

const DetailTextWrapper = styled.span`
  text-align: right;
  flex: 50%;
`

const TimelineEvent = ({
  event,
  shouldRenderLine,
  lineColor,
}: {
  event: FormBlockerElementTimelineElementEvent
  shouldRenderLine: boolean
  lineColor: 'primary' | 'inputOutline'
}) => {
  const { icon, title, detail_text, inline_description_text, inline_description_text_format } = event

  const inlineDescriptionColor =
    inline_description_text_format === FormBlockerElementTimelineElementEventInlineTextFormat.SECONDARY
      ? 'textSubtle'
      : 'textStandard'

  return (
    <TimelineEventWrapper>
      {shouldRenderLine && <Line lineColor={lineColor} />}
      {icon && <TimelineEventIcon icon={icon} />}
      <Flex flexDirection="column" gap={spacing.xs}>
        <Flex gap={spacing.xs}>
          <Text typography="labelMedium" color="textStandard">
            {title}
          </Text>
          <DetailTextWrapper>
            <Text typography="labelMedium" color="textStandard">
              {detail_text}
            </Text>
          </DetailTextWrapper>
        </Flex>
        <Text color={inlineDescriptionColor} typography="body">
          {inline_description_text}
        </Text>
      </Flex>
    </TimelineEventWrapper>
  )
}

const getLineColor = (index: number, events: FormBlockerElementTimelineElementEvent[]) => {
  const currentEvent = events[index]
  const nextEvent = events[index + 1]

  const isEventPrimary = (event: FormBlockerElementTimelineElementEvent) => {
    return (
      event.icon === FormBlockerElementTimelineElementEventIcon.HIGHLIGHT ||
      event.icon === FormBlockerElementTimelineElementEventIcon.HIGHLIGHT_COMPLETED
    )
  }

  if (nextEvent && isEventPrimary(currentEvent) && isEventPrimary(nextEvent)) {
    return 'primary'
  }
  return 'inputOutline'
}

export const TimelineElement = ({ events }: FormBlockerElementTimelineElement) => {
  if (!events) {
    logger.error(new Error('FormBlockerElementTimelineElement does not contain Events'))
    return
  }
  return (
    <PaddingInset>
      <Flex flexDirection="column" gap={spacing.l}>
        {events?.map((event, index) => {
          if (event.icon === FormBlockerElementTimelineElementEventIcon.CANCELED) {
            logger.error(new Error('Canceled TimelineEvent is not supported'))
            return null
          }
          return (
            <TimelineEvent
              key={index}
              event={event}
              shouldRenderLine={index !== events.length - 1}
              lineColor={getLineColor(index, events)}
            />
          )
        })}
      </Flex>
    </PaddingInset>
  )
}
